import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Alert, Avatar, Badge, Button, Card, Spinner } from "../../design-system";
import { Icon, SupSection, SupSelect, CategoryIcon, StatusBadge, PriorityBadge } from "./atoms";
import { STATUS, STATUS_ORDER, PRIO, PRIO_ORDER, isOpen } from "./data";
import { useSoporteNav } from "./nav";
import { useSupportMeta } from "./useMeta";
import useCurrentMembership from "../../helpers/useCurrentMembership";
import {
  getSupportTicket,
  setSupportTicketStatus,
  updateSupportTicket,
  assignSupportTicket,
  addSupportTicketComment,
  getSupportAsset,
} from "../../helpers/kronaly_helper";

const unwrap = (r) => r?.data ?? r;
const fmt = (iso) => (iso ? new Date(iso).toLocaleString("es-ES", { dateStyle: "medium", timeStyle: "short" }) : "—");

function SoporteTicketDetalle() {
  const nav = useSoporteNav();
  const { code } = useParams(); // UUID del ticket
  const { categoriesById, technicians, meta } = useSupportMeta();
  const { can } = useCurrentMembership();
  // Solo el técnico gestiona el ticket (estado, prioridad, asignación,
  // resolver, notas internas). El solicitante solo lee y comenta.
  const isTech = can("support:manage");

  const [t, setT] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [composer, setComposer] = useState("");
  const [mode, setMode] = useState("public"); // public | internal
  const [asset, setAsset] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    return getSupportTicket(code)
      .then((r) => { setT(unwrap(r)); setError(null); })
      .catch((e) => setError(e?.message || String(e)))
      .finally(() => setLoading(false));
  }, [code]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => { if (t?.number) document.title = `${t.number} · Soporte IT | Kronaly`; }, [t]);

  // Activo vinculado (contexto de hardware).
  useEffect(() => {
    if (!t?.asset_id) { setAsset(null); return; }
    let alive = true;
    getSupportAsset(t.asset_id)
      .then((r) => { if (alive) setAsset(unwrap(r)); })
      .catch(() => { if (alive) setAsset(null); });
    return () => { alive = false; };
  }, [t?.asset_id]);

  const act = async (fn) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      await load();
    } catch (e) {
      setError(e?.message || String(e));
    } finally {
      setBusy(false);
    }
  };

  const send = () => {
    if (!composer.trim()) return;
    act(async () => {
      await addSupportTicketComment(code, composer.trim(), mode === "internal");
      setComposer("");
    });
  };

  if (loading && !t) {
    return <div className="kr-page"><div style={{ display: "flex", justifyContent: "center", padding: "48px 0" }}><Spinner size={20} /></div></div>;
  }
  if (error && !t) {
    return <div className="kr-page"><Alert tone="danger" title="No se pudo cargar el ticket">{error}</Alert></div>;
  }
  if (!t) return null;

  const cat = t.category_id ? categoriesById[t.category_id] : null;
  const sub = t.subcategory_id ? categoriesById[t.subcategory_id] : null;
  const allowedNext = meta?.status_transitions?.[t.status] || [];
  const statusOptions = [t.status, ...allowedNext].map((s) => ({ value: s, label: STATUS[s]?.label || s }));

  return (
    <div className="kr-page">
      <button onClick={() => nav.back()} style={{ display: "inline-flex", alignItems: "center", gap: 6, border: "none", background: "transparent", color: "var(--kr-fg-muted)", fontSize: 12, fontWeight: 600, cursor: "pointer", padding: 0, alignSelf: "flex-start" }}>
        <Icon name="arrowLeft" size={14} />Volver a tickets
      </button>

      {error && <Alert tone="danger" title="Error">{error}</Alert>}

      {/* HEADER */}
      <Card>
        <div style={{ display: "flex", alignItems: "flex-start", gap: 14, flexWrap: "wrap" }}>
          <CategoryIcon icon={cat?.icon} color={cat?.color} size={44} iconSize={22} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
              <span style={{ fontFamily: "var(--kr-font-mono)", fontSize: 12, color: "var(--kr-fg-muted)", fontWeight: 600 }}>{t.number}</span>
              <StatusBadge status={t.status} />
              <PriorityBadge priority={t.priority} />
            </div>
            <h1 style={{ margin: 0, fontFamily: "var(--kr-font-display)", fontSize: 24, fontWeight: 700, letterSpacing: "-0.02em", color: "var(--kr-fg-strong)", lineHeight: 1.25 }}>{t.title}</h1>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 16, marginTop: 10, fontSize: 12, color: "var(--kr-fg-muted)" }}>
              {cat && <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><Icon name={cat.icon || "help"} size={13} />{cat.name}{sub ? ` · ${sub.name}` : ""}</span>}
              {t.service_label && <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><Icon name="package" size={13} />{t.service_label}</span>}
              <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><Icon name="calendar" size={13} />Creado {fmt(t.created_at)}</span>
            </div>
          </div>
        </div>

        {/* acciones rápidas (solo técnico) */}
        {isTech && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 16, paddingTop: 16, borderTop: "1px solid var(--kr-border-subtle)", alignItems: "center", opacity: busy ? 0.6 : 1, pointerEvents: busy ? "none" : "auto" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ fontSize: 11, color: "var(--kr-fg-muted)", fontWeight: 600 }}>Estado</span>
            <SupSelect value={t.status} onChange={(s) => { if (s !== t.status) act(() => setSupportTicketStatus(code, s)); }} options={statusOptions} style={{ width: 190 }} />
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ fontSize: 11, color: "var(--kr-fg-muted)", fontWeight: 600 }}>Prioridad</span>
            <SupSelect value={t.priority} onChange={(p) => { if (p !== t.priority) act(() => updateSupportTicket(code, { priority: p })); }} options={PRIO_ORDER.map((p) => ({ value: p, label: PRIO[p].label }))} style={{ width: 130 }} />
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ fontSize: 11, color: "var(--kr-fg-muted)", fontWeight: 600 }}>Responsable</span>
            <SupSelect value={t.assignee_membership_id || ""} onChange={(m) => act(() => assignSupportTicket(code, m || null))} placeholder="Sin asignar" options={technicians.map((x) => ({ value: x.membership_id, label: x.name }))} style={{ width: 160 }} />
          </div>
          <div style={{ flex: 1 }} />
          {isOpen(t.status) && (
            <Button variant="primary" icon="CheckCircle2" onClick={() => act(() => setSupportTicketStatus(code, "resuelto"))}>Resolver</Button>
          )}
        </div>
        )}
      </Card>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 320px", gap: 20, alignItems: "start" }} data-responsive-grid="2">
        {/* COLUMNA PRINCIPAL */}
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <Card>
            <SupSection eyebrow="Incidencia" title="Descripción del problema" />
            <p style={{ fontSize: 14, color: "var(--kr-fg-primary)", lineHeight: 1.6, margin: "12px 0 0", whiteSpace: "pre-wrap" }}>{t.description || "—"}</p>
          </Card>

          {/* conversación */}
          <Card padded={false}>
            <div style={{ padding: "16px 20px 0" }}>
              <SupSection eyebrow="Comunicación" title="Conversación y notas" right={<span style={{ fontSize: 11, color: "var(--kr-fg-muted)", fontFamily: "var(--kr-font-mono)" }}>{(t.comments || []).length} entradas</span>} />
            </div>
            <div style={{ padding: "16px 20px", display: "flex", flexDirection: "column", gap: 14 }}>
              {(t.comments || []).map((m) => <ThreadItem key={m.id} m={m} />)}
              {(t.comments || []).length === 0 && <div style={{ fontSize: 13, color: "var(--kr-fg-subtle)" }}>Sin mensajes todavía.</div>}
            </div>
            {/* composer */}
            <div style={{ borderTop: "1px solid var(--kr-border-subtle)", padding: 16, background: mode === "internal" ? "var(--kr-warning-50)" : "var(--kr-neutral-25)" }}>
              {isTech && (
                <div style={{ display: "flex", gap: 6, marginBottom: 10 }}>
                  <button onClick={() => setMode("public")} style={modeBtn(mode === "public", "public")}><Icon name="message" size={13} />Comentario al usuario</button>
                  <button onClick={() => setMode("internal")} style={modeBtn(mode === "internal", "internal")}><Icon name="lock" size={13} />Nota interna</button>
                </div>
              )}
              <textarea value={composer} onChange={(e) => setComposer(e.target.value)} rows={3}
                placeholder={mode === "internal" ? "Nota visible solo para soporte…" : "Escribe una respuesta para el usuario…"}
                style={{ width: "100%", border: "1px solid var(--kr-border)", borderRadius: 8, padding: "10px 12px", fontSize: 13, fontFamily: "var(--kr-font-sans)", color: "var(--kr-fg-strong)", resize: "vertical", outline: "none", boxSizing: "border-box", lineHeight: 1.5 }} />
              <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 10 }}>
                <Button variant={mode === "internal" ? "secondary" : "primary"} icon="Send" onClick={send} disabled={!composer.trim() || busy}>
                  {mode === "internal" ? "Guardar nota" : "Enviar respuesta"}
                </Button>
              </div>
            </div>
          </Card>

          {/* timeline */}
          <Card>
            <SupSection eyebrow="Trazabilidad" title="Historial de actividad" />
            <div style={{ marginTop: 14, position: "relative" }}>
              <span style={{ position: "absolute", left: 13, top: 10, bottom: 10, width: 1, background: "var(--kr-border-subtle)" }} />
              {(t.timeline || []).map((a, i) => (
                <div key={i} style={{ display: "flex", gap: 14, padding: "9px 0", position: "relative" }}>
                  <span style={{ width: 26, height: 26, borderRadius: 999, background: "#fff", border: "1px solid var(--kr-border)", display: "inline-flex", alignItems: "center", justifyContent: "center", color: "var(--kr-fg-muted)", zIndex: 1, flexShrink: 0 }}>
                    <Icon name={eventIcon(a.type)} size={12} />
                  </span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, color: "var(--kr-fg-strong)" }}>{eventLabel(a)}</div>
                  </div>
                  <span style={{ fontFamily: "var(--kr-font-mono)", fontSize: 11, color: "var(--kr-fg-muted)" }}>{fmt(a.created_at)}</span>
                </div>
              ))}
              {(t.timeline || []).length === 0 && <div style={{ fontSize: 13, color: "var(--kr-fg-subtle)" }}>Sin actividad.</div>}
            </div>
          </Card>
        </div>

        {/* CONTEXTO */}
        <div style={{ display: "flex", flexDirection: "column", gap: 16, position: "sticky", top: 0 }}>
          <Card>
            <div style={{ fontSize: 11, letterSpacing: "0.04em", textTransform: "uppercase", color: "var(--kr-fg-muted)", fontWeight: 600, marginBottom: 14 }}>Usuario afectado</div>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <Avatar name={t.requester?.name || "?"} size={44} />
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: "var(--kr-fg-strong)" }}>{t.requester?.name || "—"}</div>
                <div style={{ fontSize: 12, color: "var(--kr-fg-muted)" }}>{t.requester?.position || "—"}</div>
              </div>
            </div>
          </Card>

          {t.asset_id && (
            <Card>
              <SupSection eyebrow="Hardware · contexto" title="Activo relacionado" />
              <div onClick={() => nav.go("activo", { id: t.asset_id })} style={{ marginTop: 14, display: "flex", alignItems: "center", gap: 12, padding: "10px 12px", border: "1px solid var(--kr-border-subtle)", borderRadius: 10, background: "var(--kr-neutral-25)", cursor: "pointer" }}>
                <CategoryIcon icon={asset?.type?.icon} color="neutral" size={36} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "var(--kr-fg-strong)" }}>{asset ? [asset.brand, asset.model].filter(Boolean).join(" ") || asset.code : "Cargando…"}</div>
                  <div style={{ fontSize: 11, color: "var(--kr-fg-muted)", fontFamily: "var(--kr-font-mono)" }}>{asset?.code}</div>
                </div>
                <Icon name="chevronRight" size={15} stroke="var(--kr-fg-subtle)" />
              </div>
            </Card>
          )}

          {t.resource_id && (() => {
            const res = (meta?.resources || []).find((r) => r.id === t.resource_id);
            return (
              <Card>
                <SupSection eyebrow="Software · contexto" title="Recurso relacionado" />
                <div onClick={() => nav.go("recursos")} style={{ marginTop: 14, display: "flex", alignItems: "center", gap: 12, padding: "10px 12px", border: "1px solid var(--kr-border-subtle)", borderRadius: 10, background: "var(--kr-neutral-25)", cursor: "pointer" }}>
                  <CategoryIcon icon={res?.icon || "cloud"} color={res?.color || "neutral"} size={36} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: "var(--kr-fg-strong)" }}>{res?.name || "Recurso"}</div>
                    <div style={{ fontSize: 11, color: "var(--kr-fg-muted)" }}>{res?.type || ""}</div>
                  </div>
                  <Icon name="chevronRight" size={15} stroke="var(--kr-fg-subtle)" />
                </div>
              </Card>
            );
          })()}

          <Card>
            <SupSection eyebrow="SLA" title="Objetivos" />
            <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 8, fontSize: 12 }}>
              <SlaRow label="1ª respuesta" due={t.sla_response_due_at} done={t.first_response_at} />
              <SlaRow label="Resolución" due={t.sla_resolution_due_at} done={t.resolved_at} />
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}

function modeBtn(active, kind) {
  const c = kind === "internal" ? "var(--kr-warning-600)" : "var(--kr-action)";
  return {
    display: "inline-flex", alignItems: "center", gap: 6, height: 30, padding: "0 12px",
    border: `1px solid ${active ? c : "var(--kr-border)"}`,
    background: active ? "#fff" : "transparent",
    color: active ? c : "var(--kr-fg-muted)",
    borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: "pointer",
    boxShadow: active ? "var(--kr-shadow-xs)" : "none",
  };
}

function ThreadItem({ m }) {
  const internal = m.kind === "internal_note";
  const agent = m.kind === "agent";
  return (
    <div style={{ display: "flex", gap: 12 }}>
      <Avatar name={m.author_user_id ? "·" : "?"} size={32} status={agent ? "working" : undefined} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
          {internal ? <Badge tone="warning" variant="soft"><Icon name="lock" size={10} style={{ marginRight: 3 }} />Nota interna</Badge>
            : agent ? <Badge tone="brand" variant="soft">Soporte</Badge>
            : <Badge tone="neutral" variant="soft">Usuario</Badge>}
          <span style={{ fontSize: 11, color: "var(--kr-fg-muted)", fontFamily: "var(--kr-font-mono)" }}>{m.created_at ? new Date(m.created_at).toLocaleString("es-ES", { dateStyle: "short", timeStyle: "short" }) : ""}</span>
        </div>
        <div style={{ fontSize: 13, color: internal ? "var(--kr-warning-700)" : "var(--kr-fg-primary)", lineHeight: 1.55, padding: "10px 12px", background: internal ? "var(--kr-warning-50)" : agent ? "var(--kr-action-soft-bg)" : "var(--kr-neutral-50)", border: internal ? "1px solid var(--kr-warning-100)" : "none", borderRadius: 8, whiteSpace: "pre-wrap" }}>{m.body}</div>
      </div>
    </div>
  );
}

function SlaRow({ label, due, done }) {
  const breached = due && !done && new Date(due) < new Date();
  const tone = done ? "var(--kr-success-700)" : breached ? "var(--kr-danger-700)" : "var(--kr-fg-strong)";
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
      <span style={{ color: "var(--kr-fg-muted)" }}>{label}</span>
      <span style={{ fontFamily: "var(--kr-font-mono)", color: tone, fontWeight: 600 }}>
        {done ? "cumplido" : breached ? "vencido" : due ? new Date(due).toLocaleString("es-ES", { dateStyle: "short", timeStyle: "short" }) : "—"}
      </span>
    </div>
  );
}

function eventIcon(type) {
  return {
    created: "plus", assigned: "users", unassigned: "users", status_changed: "refresh",
    comment_added: "message", updated: "edit",
  }[type] || "more";
}
function eventLabel(a) {
  const p = a.payload || {};
  switch (a.type) {
    case "created": return "Ticket creado";
    case "assigned": return "Asignado a un técnico";
    case "unassigned": return "Responsable retirado";
    case "status_changed": return `Estado: ${STATUS[p.from]?.label || p.from} → ${STATUS[p.to]?.label || p.to}`;
    case "comment_added": return p.kind === "internal_note" ? "Nota interna añadida" : "Comentario añadido";
    case "updated": return "Ticket actualizado";
    default: return a.type;
  }
}

// Wrapper de ruta: remonta al cambiar de ticket.
export default function SoporteTicketDetallePage() {
  const { code } = useParams();
  return <SoporteTicketDetalle key={code} />;
}
